<template>
  <Card>
    <template #title="">
      <div class="p-d-flex p-justify-between">
        รายงานอายุของเอกสาร
        <div>
          <BackButton></BackButton>
        </div>
      </div>
    </template>
    <template #content="">
      <div class="p-grid">
        <div class="p-col-12 p-lg-3 p-md-4 p-fluid p-field">
          <span class="p-float-label">
            <Calendar
              id="selectedDate"
              v-model="selectedDate"
              selectionMode="range"
            ></Calendar>
            <label for="selectedDate">ระบุวันที่</label>
          </span>
        </div>
        <div class="p-col-fixed">
          <Button
            icon="pi pi-search"
            label="ค้นหา"
            @click="onQueryReport"
          ></Button>
        </div>
        <div class="p-col-12">
          <DataTable
            :value="reports"
            paginator
            :rows="10"
            class="p-datatable-sm"
            v-model:filters="filters"
            filterDisplay="row"
            :globalFilterFields="[
              'id',
              'age',
              'status',
              'employee.id',
              'employee.name',
              'supervisor.id',
              'supervisor.name',
            ]"
          >
            <template #header="">
              <div class="p-d-flex p-justify-end">
                <span class="p-input-icon-left">
                  <i class="pi pi-search"></i>
                  <InputText v-model="filters['global'].value" />
                </span>
              </div>
            </template>
            <template #empty="">
              ไม่พบข้อมูล
            </template>
            <Column header="รหัสเอกสาร" field="id">
              <template #filter="{filterModel, filterCallback}">
                <InputText
                  v-model="filterModel.value"
                  @input="filterCallback"
                ></InputText>
              </template>
            </Column>
            <Column header="วันที่เริ่มทำแบบทดสอบ" field="date">
              <template #body="props">
                {{ formatAppDate(props.data.date) }}
              </template>
            </Column>
            <Column header="พนักงาน" field="employee.id">
              <template #body="props">
                {{ props.data.employee.id }} : {{ props.data.employee.prename
                }}{{ props.data.employee.name }}
                {{ props.data.employee.surname }}
              </template>
            </Column>
            <Column header="หัวหน้างาน" field="supervisor.id">
              <template #body="props">
                <span v-if="props.data.supervisor">
                  {{ props.data.supervisor.id }} :
                  {{ props.data.supervisor.prename
                  }}{{ props.data.supervisor.name }}
                  {{ props.data.supervisor.surname }}
                </span>
                <span v-else>ไม่ระบุ</span>
              </template>
            </Column>
            <Column
              header="อายุเอกสาร"
              field="age"
              filterField="age"
              dataType="numeric"
              sortable
              headerStyle="width: 10rem"
              bodyStyle="width: 10rem"
            >
              <template #filter="{filterModel, filterCallback}">
                <InputText
                  v-model="filterModel.value"
                  @input="filterCallback()"
                  type="number"
                ></InputText>
              </template>
              <template #body="props"> {{ props.data.age }} วัน </template>
            </Column>
            <Column header="สถานะเอกสาร" field="status">
              <template #filter="{filterModel, filterCallback}">
                <Dropdown
                  v-model="filterModel.value"
                  :options="statuses"
                  @change="filterCallback()"
                  showClear
                ></Dropdown>
              </template>
              <template #body="props">
                <StatusBadge :status="props.data.status"></StatusBadge>
              </template>
            </Column>
            <Column headerStyle="width: 4rem" bodyStyle="width:4rem">
              <template #body="props">
                <Button
                  class="p-button-rounded"
                  icon="pi pi-pencil"
                  @click="onGoToDocument(props.data.key)"
                ></Button>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
  </Card>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { usePlugin } from "@/plugins";
import {
  DocumentReportDto,
  GetAgingReportResponse,
  GetAgingReportRequest,
} from "@/types";
import StatusBadge from "@/components/StatusBadge.vue";
import { FilterMatchMode, FilterOperator } from "primevue/api";
import { useSessionState } from "@/store";
import { useSessionStorage } from "@vueuse/core";

export default defineComponent({
  name: "AgingReport",
  components: { StatusBadge },
  setup() {
    const {
      setLoading,
      axios,
      success,
      error,
      dayjs,
      onRouterPush,
      formatAppDate,
    } = usePlugin();
    const selectedDate = useSessionStorage<Date[]>("agingReport-selectedDate", [
      dayjs()
        .startOf("month")
        .toDate(),
      dayjs()
        .endOf("month")
        .toDate(),
    ]);
    const reports = ref<DocumentReportDto[]>([]);
    const filters: { [key: string]: any } = useSessionStorage(
      "agingReport-filters",
      {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        id: { value: null, matchMode: FilterMatchMode.CONTAINS },
        "supervisor.id": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "employee.id": { value: null, matchMode: FilterMatchMode.CONTAINS },
        age: {
          value: null,
          matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO,
        },
        status: { value: null, matchMode: FilterMatchMode.EQUALS },
      }
    );

    const statuses = computed(() => [
      ...new Set(reports.value.map((x) => x.status).sort()),
    ]);

    const onQueryReport = async () => {
      setLoading(true);
      try {
        const result = await axios.post<GetAgingReportResponse>(
          "api/v1/services/report/aging",
          {
            selectedDate: selectedDate.value,
          } as GetAgingReportRequest
        );

        reports.value = result.data.reports ?? [];
        success("ดึงข้อมูลสำเร็จแล้ว");
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onGoToDocument = (id: string) => {
      if (id.indexOf("|") >= 0) {
        const keys = id.split("|");
        onRouterPush({
          name: "viewAssessment",
          params: {
            documentId: keys[0],
            questionId: keys[1],
            employeeId: keys[2],
          },
        });
      } else {
        onRouterPush({ name: "positionAssessment", params: { id: id } });
      }
    };

    onMounted(() => {
      onQueryReport();
    });

    //Convert to date just before setup complete
    selectedDate.value[0] = dayjs(selectedDate.value[0]).toDate();
    selectedDate.value[1] = dayjs(selectedDate.value[1]).toDate();

    return {
      selectedDate,
      onQueryReport,
      reports,
      onGoToDocument,
      formatAppDate,
      filters,
      statuses,
    };
  },
});
</script>

<style></style>
